<template>
  <!-- 图片预览遮罩层 -->
  <div v-show="visible" class="preview-mask" @click.self="close">
    <!-- 关闭按钮 -->
    <div class="preview-close" @click="close">
      <Icon type="md-close" size="24" color="#fff" />
    </div>

    <!-- 图片容器 -->
    <div class="preview-container">
      <!-- 上一张按钮 -->
      <div v-if="images.length > 1" class="nav-btn prev-btn" @click="prev">
        <Icon type="ios-arrow-back" size="30" color="#fff" />
      </div>

      <!-- 图片显示区域 -->
      <div class="image-area">
        <img 
          v-if="currentIndex >= 0 && images[currentIndex]"
          :src="getImageUrl(images[currentIndex])"
          class="preview-image"
          @click="close"
          :alt="getImageAlt(images[currentIndex])"
        />
      </div>

      <!-- 下一张按钮 -->
      <div v-if="images.length > 1" class="nav-btn next-btn" @click="next">
        <Icon type="ios-arrow-forward" size="30" color="#fff" />
      </div>

      <!-- 缩略图导航 -->
      <div v-if="images.length > 1" class="thumbnail-nav">
        <div 
          v-for="(img, index) in images" 
          :key="getImageKey(img, index)"
          class="thumbnail-item"
          :class="{ active: index === currentIndex }"
          @click="switchTo(index)"
        >
          <img :src="getImageUrl(img)" />
        </div>
      </div>

      <!-- 图片信息 -->
      <div v-if="showInfo" class="image-info">
        <span class="image-index">
          {{ currentIndex + 1 }} / {{ images.length }}
        </span>
      </div>
    </div>

    <!-- 键盘导航提示 -->
    <div v-if="images.length > 1 && showKeyboardHint" class="keyboard-hint">
      <span>← → 切换图片 | ESC 关闭</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImagePreview',
  
  props: {
    // 图片数组
    images: {
      type: Array,
      default: () => []
    },
    // 是否显示
    visible: {
      type: Boolean,
      default: false
    },
    // 初始索引
    initialIndex: {
      type: Number,
      default: 0
    },
    // 是否显示图片信息
    showInfo: {
      type: Boolean,
      default: true
    },
    // 是否显示键盘提示
    showKeyboardHint: {
      type: Boolean,
      default: true
    },
    // 图片URL字段名
    urlField: {
      type: String,
      default: 'url'
    },
    // 图片名称字段名
    nameField: {
      type: String,
      default: 'name'
    }
  },
  
  data() {
    return {
      currentIndex: 0
    };
  },
  
  watch: {
    visible(newVal) {
      if (newVal) {
        // 显示时禁止背景滚动
        document.body.style.overflow = 'hidden';
        this.currentIndex = this.initialIndex;
        this.addKeyboardListeners();
      } else {
        // 关闭时恢复背景滚动
        document.body.style.overflow = '';
        this.removeKeyboardListeners();
      }
    },
    
    initialIndex(newIndex) {
      if (newIndex >= 0 && newIndex < this.images.length) {
        this.currentIndex = newIndex;
      }
    },
    
    images(newImages) {
      // 如果图片数组变化，确保当前索引有效
      if (this.currentIndex >= newImages.length) {
        this.currentIndex = Math.max(0, newImages.length - 1);
      }
    }
  },
  
  computed: {
    currentImage() {
      return this.images[this.currentIndex] || {};
    }
  },
  
  methods: {
    /**
     * 获取图片URL
     */
    getImageUrl(img) {
      if (!img) return '';
      return img[this.urlField] || img.url || img;
    },
    
    /**
     * 获取图片alt文本
     */
    getImageAlt(img) {
      if (!img) return '';
      return img[this.nameField] || img.name || '';
    },
    
    /**
     * 获取图片key
     */
    getImageKey(img, index) {
      if (img.id) return img.id;
      if (img[this.urlField]) return img[this.urlField];
      return `img-${index}`;
    },
    
    /**
     * 上一张图片
     */
    prev() {
      if (this.images.length <= 1) return;
      
      this.currentIndex--;
      if (this.currentIndex < 0) {
        this.currentIndex = this.images.length - 1;
      }
      this.$emit('change', this.currentIndex, this.currentImage);
    },
    
    /**
     * 下一张图片
     */
    next() {
      if (this.images.length <= 1) return;
      
      this.currentIndex++;
      if (this.currentIndex >= this.images.length) {
        this.currentIndex = 0;
      }
      this.$emit('change', this.currentIndex, this.currentImage);
    },
    
    /**
     * 切换到指定图片
     */
    switchTo(index) {
      if (index >= 0 && index < this.images.length) {
        this.currentIndex = index;
        this.$emit('change', this.currentIndex, this.currentImage);
      }
    },
    
    /**
     * 关闭预览
     */
    close() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    
    /**
     * 添加键盘事件监听
     */
    addKeyboardListeners() {
      window.addEventListener('keydown', this.handleKeydown);
    },
    
    /**
     * 移除键盘事件监听
     */
    removeKeyboardListeners() {
      window.removeEventListener('keydown', this.handleKeydown);
    },
    
    /**
     * 键盘事件处理
     */
    handleKeydown(event) {
      if (!this.visible) return;
      
      switch (event.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.prev();
          event.preventDefault();
          break;
        case 'ArrowRight':
          this.next();
          event.preventDefault();
          break;
      }
    }
  },
  
  mounted() {
    this.currentIndex = this.initialIndex;
  },
  
  beforeDestroy() {
    this.removeKeyboardListeners();
    // 确保恢复背景滚动
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
/* 预览遮罩层样式 */
.preview-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.3s ease;
}

/* 关闭按钮 */
.preview-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s;
  z-index: 10001;
}

.preview-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* 预览容器 */
.preview-container {
  position: relative;
  width: 90%;
  height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 图片显示区域 */
.image-area {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 20px;
}

/* 预览图片 */
.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  cursor: zoom-out;
  transition: transform 0.3s ease;
}

.preview-image:hover {
  transform: scale(1.02);
}

/* 导航按钮 */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s;
  z-index: 10000;
}

.nav-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.prev-btn {
  left: 20px;
}

.next-btn {
  right: 20px;
}

/* 缩略图导航 */
.thumbnail-nav {
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  margin: 0 100px;
  z-index: 10000;
}

.thumbnail-item {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.3s, transform 0.3s;
  opacity: 0.6;
}

.thumbnail-item:hover {
  transform: scale(1.1);
  opacity: 0.8;
}

.thumbnail-item.active {
  border-color: #2d8cf0;
  opacity: 1;
  transform: scale(1.1);
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 图片信息 */
.image-info {
  position: absolute;
  top: 20px;
  left: 20px;
  color: white;
  font-size: 16px;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 8px 16px;
  border-radius: 20px;
  z-index: 10000;
}

/* 键盘提示 */
.keyboard-hint {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  z-index: 10000;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .thumbnail-nav {
    margin: 0 20px;
    gap: 4px;
  }
  
  .thumbnail-item {
    width: 40px;
    height: 40px;
  }
  
  .nav-btn {
    width: 40px;
    height: 40px;
  }
  
  .preview-close {
    top: 10px;
    right: 10px;
  }
}
</style>